import { AutoComplete, Divider } from 'antd'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { DefaultOptionType } from 'antd/es/select'

import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
config.autoAddCss = false
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '@/src/lib'

type PlaceholderWithIconProps = {
  icon: IconProp
  type: string
}

const PlaceholderWithIcon: React.FC<PlaceholderWithIconProps> = ({
  icon,
  type,
}) => {
  const text =
    type === 'full_name'
      ? 'E.g.: "Jane Doe"'
      : 'E.g.: "Cardiology" or "COVID 19"'

  return (
    <div className='flex items-center justify-start'>
      <div className='flex justify-center items-center mr-2'>
        <FontAwesomeIcon icon={icon} />
      </div>
      {text}
    </div>
  )
}

interface OptionType extends DefaultOptionType {
  highlightedCategory?: string
  group?: string
}

interface GroupedOptionType {
  label: string
  options: OptionType[]
  key: string
}

interface OptionWithHasOptions extends GroupedOptionType {
  hasOptions: boolean
}

interface MultiSearchBarProps {
  value: string
  onChange: (value: string, option?: any) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onBlur: () => void
  onSelect: (value: string) => void
  onClear: () => void
  options: (GroupedOptionType | OptionType)[] | undefined
  searchCategory: string
}

export const MultiSearchBar = ({
  value,
  onChange,
  onKeyDown,
  onBlur,
  onSelect,
  onClear,
  options = [],
  searchCategory,
}: MultiSearchBarProps) => {
  const renderOption = (
    option: OptionType | GroupedOptionType,
    isFirstGroup: boolean
  ): OptionType | GroupedOptionType => {
    if ('options' in option) {
      const icon =
        typeof option.label === 'string' && option.label.includes('Specialties')
          ? 'user-doctor'
          : 'magnifying-glass'

      // Flatten and transform the options
      const addedCategories = new Set<string>()
      const categoryOptions: OptionType[] = []
      option.options.forEach((opt: OptionType, index: number) => {
        if (
          opt.highlightedCategory &&
          !addedCategories.has(opt.highlightedCategory)
        ) {
          categoryOptions.push({
            ...opt,
            key: `${option.key}-category-${index}-highlightedCategory`,
            value: opt.value,
            label: (
              <div className='flex items-center justify-between'>
                <span
                  dangerouslySetInnerHTML={{
                    __html: opt.highlightedCategory as string,
                  }}
                />
              </div>
            ),
          })
          addedCategories.add(opt.highlightedCategory)
        }
      })

      return {
        ...option,
        label: (
          <>
            {!isFirstGroup && <Divider style={{ margin: '8px 0' }} />}
            <div className='flex items-center justify-between py-1'>
              <span className='text-lg font-light text-primary'>
                <FontAwesomeIcon icon={icon} className='mr-1' /> {option.label}
              </span>
            </div>
          </>
        ),
        options: categoryOptions,
      }
    }

    return option
  }

  // Render highlighted physician name
  const renderPhysicianName = (option: OptionType) => {
    if (!option.highlightedName) {
      return option
    }

    return {
      ...option,
      label: (
        <div className='flex items-center justify-between'>
          <span
            dangerouslySetInnerHTML={{
              __html: option.highlightedName as string,
            }}
          />
        </div>
      ),
    }
  }

  const formattedOptions: (OptionType | GroupedOptionType)[] =
    searchCategory === 'full_name'
      ? options && options.map((option) => renderPhysicianName(option))
      : options
          .map((option): OptionWithHasOptions | OptionType => ({
            ...option,
            hasOptions: 'options' in option && option.options.length > 0,
          }))
          .filter(
            (option): option is GroupedOptionType =>
              'options' in option && option.options.length > 0
          )
          .sort((a, b) => {
            if ('options' in a && 'options' in b) {
              const aKey = a.key || ''
              const bKey = b.key || ''

              if (aKey.includes('conditions') && bKey.includes('specialties'))
                return -1
              if (bKey.includes('conditions') && aKey.includes('specialties'))
                return 1

              return aKey.localeCompare(bKey)
            }
            return 0
          })
          .map((option, index) => renderOption(option, index === 0))

  return (
    <div className='flex flex-col w-full'>
      <AutoComplete
        data-testid='physician-input'
        allowClear
        className='custom-select w-full lg:min-w-[420px]'
        style={{
          height: '5rem',
          borderRight: 'none',
        }}
        aria-label='Search for physician name, specialty or disease state'
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onSelect={onSelect}
        options={formattedOptions}
        placeholder={
          <PlaceholderWithIcon
            icon={'magnifying-glass'}
            type={searchCategory}
          />
        }
        size='large'
        onClear={onClear}
        popupMatchSelectWidth={true}
      />
    </div>
  )
}
